import type { CreatorBlock, RiskType } from "@unbox/api";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Table } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { CreatorBlockedBadge } from "@/components/blocklist/creator-blocked-badge";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { cx } from "@/utils/cx";
import { formatDate } from "@/utils/date-conversion";
import "../../../table-styles.css";
import { useSeenRows } from "@/components/application/table/use-seen-rows";
import { FLAGGED_VIDEOS_TABLE_KEY } from "@/pages/safety-beta/flagged/components/VideoProgressRow";
import type { ActivityItem } from "../../components/ActivityFeed";
import { VideoProgressRow } from "../../components/VideoProgressRow";

export type ResolvedVideo = {
  id: string;
  title: string;
  creator: string;
  creatorId?: string;
  views: string;
  postedAgo: string;
  postedAtDate: string;
  topFlags: { name: string; severity: "critical" | "high" | "medium" | "low" } | null;
  flagCount: number;
  urlFirstFrame?: string | null;
  isInAdCampaign?: boolean;
  outcome: string;
  resolvedAt?: string | Date | null;
  creatorBlock?: CreatorBlock | null;
  riskDetails: {
    name: string;
    severity: "critical" | "high" | "medium" | "low";
    description?: string;
    evidenceType: RiskType;
    confirmedBy?: string;
    confirmedAt?: string;
    evidence: { ts?: string; text: string }[];
  }[];
  activity: ActivityItem[];
};

type ResolvedVideoTableProps = {
  videos: ResolvedVideo[];
  total: number;
  selectedId: string;
  onSelect: (id: string) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  // True while the detail panel is closed — widens the Risks/Outcome columns (and
  // shrinks Views) to use the reclaimed space instead of leaving it all in Views.
  fullWidth?: boolean;
};

export const ResolvedVideoTable = ({
  videos,
  total,
  selectedId,
  onSelect,
  hasActiveFilters,
  onClearFilters,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  fullWidth,
}: ResolvedVideoTableProps) => {
  const { markSeen, seenCount } = useSeenRows(FLAGGED_VIDEOS_TABLE_KEY);
  const { scrollRef, onScroll } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onLoadMore,
    itemCount: videos.length,
  });

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className={cx(
        fullWidth ? "w-full" : "w-[calc(57%-0.5rem)]",
        "shrink-0 min-w-[520px] border border-border-primary rounded-md overflow-y-auto bg-white h-full",
      )}
    >
      <Table
        size="sm"
        aria-label="Risk Resolved videos"
        onRowAction={(key) => {
          markSeen(key as string);
          onSelect(key as string);
        }}
        className="table-fixed"
      >
        <Table.Header className="bg-white">
          <Table.Head
            id="video"
            label={`${total} Videos`}
            isRowHeader
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-video-full" : "flagged-progress-table-col-video-compact",
            )}
          />
          <Table.Head
            id="views"
            label="Views"
            className={cx(
              "px-[12px] py-[12px] [&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-views-full" : "flagged-progress-table-col-views-compact",
            )}
          />
          <Table.Head
            id="risks"
            label="Risks"
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary",
              fullWidth ? "flagged-progress-table-col-risks-full" : "flagged-progress-table-col-risks-compact",
            )}
          />
          <Table.Head
            id="outcome"
            label="Outcome"
            className={cx(
              "[&_span]:text-quaternary [&_span]:text-xs [&_span]:font-semibold [&_svg]:text-quaternary pl-[12px]",
              fullWidth ? "flagged-progress-table-col-status-full" : "flagged-progress-table-col-status-compact",
            )}
          />
        </Table.Header>
        <Table.Body
          items={videos}
          dependencies={[seenCount]}
          renderEmptyState={() => (
            <div className="h-full flex items-center justify-center">
              <UnboxTableEmptyStateContent
                rows={[]}
                hasActiveFilters={hasActiveFilters}
                filteredEmptyState={{ onClearFilters }}
              />
            </div>
          )}
        >
          {(video) => (
            <VideoProgressRow
              id={video.id}
              title={video.title}
              creator={video.creator}
              views={video.views}
              postedAgo={video.postedAgo}
              topFlags={video.topFlags}
              flagCount={video.flagCount}
              urlFirstFrame={video.urlFirstFrame}
              isInAdCampaign={video.isInAdCampaign}
              selected={video.id === selectedId}
              statusContent={
                <div className="flex flex-col gap-0.5 min-w-0">
                  {video.creatorBlock ? (
                    <CreatorBlockedBadge block={video.creatorBlock} label="Blocked" />
                  ) : (
                    <Tooltip title={video.outcome}>
                      <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
                        <Badge size="sm" color="blue" className="inline-block w-full truncate align-middle">
                          {video.outcome}
                        </Badge>
                      </TooltipTrigger>
                    </Tooltip>
                  )}
                  {video.resolvedAt && <span className="text-xs text-quaternary">{formatDate(video.resolvedAt)}</span>}
                </div>
              }
              fullWidth={fullWidth}
            />
          )}
        </Table.Body>
      </Table>
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-3">
          <LoadingIndicator size="xs" />
        </div>
      )}
    </div>
  );
};
